const PREFIX = "brandingLogosCache";

/** The pre-scoping key. Still cleared, so an old tab's entry cannot be read. */
export const BRANDING_LOGOS_CACHE_KEY = PREFIX;

/**
 * One entry per identity.
 *
 * A single shared key rendered the previous tenant's logo, icon and favicon
 * after a switch, because the cache is read before the fetch answers and the
 * reset never cleared `logos` the way it cleared the colours. Keying by user
 * means a different identity simply misses, so there is nothing stale to show
 * and the same user keeps the flash-free first paint the cache exists for.
 */
export const brandingLogosCacheKey = (userId?: string | null): string | null =>
  userId ? `${PREFIX}:${userId}` : null;

export function readBrandingLogosCache<T = unknown>(
  userId?: string | null,
): T | null {
  const key = brandingLogosCacheKey(userId);
  if (!key) return null;
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    // Unavailable in private mode, or a shape an older build wrote.
    return null;
  }
}

export function writeBrandingLogosCache(
  userId: string | null | undefined,
  logos: unknown,
): void {
  const key = brandingLogosCacheKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(logos));
  } catch {
    // localStorage may be unavailable in private mode; ignore
  }
}

/** Every identity's entry, for impersonation start and end. */
export function clearBrandingLogosCache() {
  try {
    const stale = Object.keys(localStorage).filter(
      (key) => key === PREFIX || key.startsWith(`${PREFIX}:`),
    );
    stale.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage may be unavailable in private mode; ignore
  }
}
